<script setup>
import { Icon } from "@iconify/vue";
import VueApexCharts from "vue3-apexcharts";

const emits = defineEmits(["onCancel"]);

const props = defineProps({
  userInfo: {
    type: Object,
    default: () => ({}),
  },
});

const appStore = useAppStore();

const sigilStatsDialog = ref(false);
const loading = ref(false);
const summary = ref({});
const page = ref(1);
const itemsPerPage = ref(5);
const historyData = ref([]);
const currentPrice = ref(0.056);
const holdersDialogRef = ref(null);
const avaxDisplayConfig = {
  forceUnit: "AVAX",
  decimalsByUnit: { AVAX: 10 },
};

const openDialog = async () => {
  sigilStatsDialog.value = true;
  const p1 = fetchStats();
  const p2 = fetchSummary();

  await Promise.all([p1, p2]);
};

const closeDialog = () => {
  emits("onCancel");
  sigilStatsDialog.value = false;
};

const fetchStats = async () => {
  loading.value = true;
  try {
    const params = {
      page: page.value,
      itemsPerPage: itemsPerPage.value,
    };

    const response = await getSubjectHistory(props.userInfo?.uid, params);

    const data = response.data || response || [];
    historyData.value = data;

    if (data && data.length > 0) {
      const latestEntry = data[data.length - 1];
      currentPrice.value = parseFloat(latestEntry.value) || 0;
    }
  } catch (error) {
    console.error("Error fetching sigil stats:", error);
    historyData.value = [];
    currentPrice.value = 0;
  } finally {
    loading.value = false;
  }
};

const fetchSummary = async () => {
  loading.value = true;
  try {
    const response = await getSubjectSummary(props.userInfo?.uid);
    summary.value = response;
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    loading.value = false;
  }
};

const priceDisplayMeta = computed(() => {
  const values = [];

  if (historyData.value && historyData.value.length > 0) {
    values.push(
      ...historyData.value
        .map((item) => parseFloat(item.value) || 0)
        .filter((val) => Number.isFinite(val))
    );
  }

  if (Number.isFinite(currentPrice.value)) {
    values.push(currentPrice.value);
  }

  const maxValue = values.length ? Math.max(...values) : 0;
  return formatAvaxDisplay(maxValue || 0, {
    isAvaxValue: true,
    ...avaxDisplayConfig,
  });
});

const chartSeries = computed(() => {
  if (!historyData.value || historyData.value.length === 0) {
    return [];
  }

  const sortedData = [...historyData.value].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const multiplier = priceDisplayMeta.value.multiplier || 1;
  const chartData = sortedData.map((item, index) => ({
    x: index,
    y: (parseFloat(item.value) || 0) * multiplier,
  }));

  return [
    {
      name: `Price (${priceDisplayMeta.value.unit})`,
      data: chartData,
    },
  ];
});

const chartOptions = computed(() => {
  const multiplier = priceDisplayMeta.value.multiplier || 1;
  const forcedUnit = priceDisplayMeta.value.unit;
  const forcedDecimals = priceDisplayMeta.value.decimals;

  return {
    chart: {
      type: "area",
      height: 200,
      background: "transparent",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
      offsetY: 0,
      sparkline: {
        enabled: false,
      },
    },
    colors: ["#e91e63"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
      colors: ["#e91e63"],
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.8,
        opacityTo: 0.1,
        stops: [0, 100],
        colorStops: [
          {
            offset: 0,
            color: "#e91e63",
            opacity: 0.8,
          },
          {
            offset: 100,
            color: "#e91e63",
            opacity: 0.1,
          },
        ],
      },
    },
    grid: {
      show: true,
      borderColor: "#444",
      strokeDashArray: 0,
      position: "back",
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      type: "numeric",
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        show: false,
      },
      min: 0,
      tickAmount: 6,
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          colors: "#9CA3AF",
          fontSize: "12px",
        },
        formatter: function (val) {
          const avaxValue = (val || 0) / multiplier;
          const display = formatAvaxDisplay(avaxValue, {
            isAvaxValue: true,
            forceUnit: forcedUnit,
            decimalsByUnit: { [forcedUnit]: forcedDecimals },
          });
          return `${display.value} ${display.unit}`;
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    tooltip: {
      enabled: true,
      theme: "dark",
      style: {
        fontSize: "12px",
        fontFamily: "inherit",
      },
      x: {
        show: true,
        formatter: function (val) {
          const sortedData = [...historyData.value].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          if (sortedData && sortedData[val]) {
            const date = new Date(sortedData[val].createdAt);
            return date.toLocaleDateString();
          }
          return `Point ${val}`;
        },
      },
      y: {
        formatter: function (val) {
          const avaxValue = (val || 0) / multiplier;
          const display = formatAvaxDisplay(avaxValue, {
            isAvaxValue: true,
            forceUnit: forcedUnit,
            decimalsByUnit: { [forcedUnit]: forcedDecimals },
          });
          return `${display.value} ${display.unit}`;
        },
      },
    },
    legend: {
      show: false,
    },
    theme: {
      mode: "dark",
    },
  };
});

const totalVolumeDisplay = computed(() =>
  formatAvaxDisplay(summary.value?.totalVolumeSpent || 0, {
    isAvaxValue: true,
    ...avaxDisplayConfig,
  })
);

const copyWalletAddress = async () => {
  try {
    await navigator.clipboard.writeText(props.userInfo?.wallet_address);

    appStore.addAlert("Wallet address copied to clipboard!", {
      type: "success",
      color: "green",
      icon: "mdi-check-circle",
      timeout: 3000,
    });
  } catch (error) {
    console.error("Failed to copy wallet address:", error);
  }
};

const openHoldersDialog = async () => {
  holdersDialogRef.value?.openDialog();
};

defineExpose({ openDialog, sigilStatsDialog, closeDialog });
</script>

<template>
  <v-dialog
    v-model="sigilStatsDialog"
    class="tw-rounded-2xl !tw-z-[2010]"
    width="600"
    persistent
    scrim="#000"
    opacity="0.5"
  >
    <dialog-close-btn
      :icon="'mdi-close-circle-outline'"
      :iconSize="'32'"
      @onSubmitButton="closeDialog"
    />

    <v-card
      class="!tw-rounded-2xl !tw-px-6 !tw-py-4 tw-bg-[#1a1a1a]"
      min-height="500"
      :loading="loading"
    >
      <v-card-title
        class="tw-text-center tw-p-0 tw-text-xl tw-font-bold tw-text-white tw-mb-4"
      >
        Sigil Stats
      </v-card-title>

      <v-card-text class="!tw-px-0">
        <div
          class="tw-flex tw-justify-between tw-items-center tw-text-center tw-mb-6 tw-px-9"
          v-if="props.userInfo"
        >
          <div class="tw-text-white tw-text-lg tw-font-bold tw-mb-1">
            {{ props.userInfo?.firstName }}
          </div>

          <div class="tw-flex tw-items-center tw-font-bold tw-gap-2">
            <span class="">
              {{ shortenAddress(props.userInfo?.wallet_address) || "" }}
            </span>
            <button
              @click="copyWalletAddress"
              class="tw-flex tw-items-center tw-justify-center hover:tw-text-white tw-rounded"
              title="Copy wallet address"
            >
              <Icon icon="mdi-content-copy" :width="16" :height="16" />
            </button>
          </div>
        </div>

        <div class="tw-mb-6">
          <div class="tw-grid tw-grid-cols-3 tw-gap-4 tw-mb-4 tw-px-9">
            <v-sheet class="stat-card" color="transparent">
              <div class="stat-number">
                {{ summary.numberOfKeysYouHold || 0 }}
              </div>
              <div class="stat-label">YOU HOLD</div>
            </v-sheet>

            <v-sheet
              class="stat-card tw-cursor-pointer hover:!tw-border-white/70 focus:!tw-border-white/70"
              color="transparent"
              @click="openHoldersDialog"
            >
              <div class="stat-number">{{ summary.totalHolders || 0 }}</div>
              <div class="stat-label">HOLDERS</div>

              <holders-dialog ref="holdersDialogRef" :userInfo="userInfo" />
            </v-sheet>

            <v-sheet class="stat-card" color="transparent">
              <div class="stat-number">{{ summary.totalHoldings || 0 }}</div>
              <div class="stat-label">HOLDINGS</div>
            </v-sheet>
          </div>

          <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4 tw-px-20">
            <v-sheet class="stat-card" color="transparent">
              <div class="stat-number">
                <span class="tw-text-[#02C215]"
                  >{{ summary.numberOfBuys || 0 }} BUY</span
                >/<br />
                <span class="tw-text-[#D80202]"
                  >{{ summary.numberOfSells || 0 }} SELL</span
                >
              </div>
            </v-sheet>

            <v-sheet class="stat-card" color="transparent">
              <div class="stat-number">{{ summary.totalKeysBought || 0 }}</div>
              <div class="stat-label">TOTAL SUPPLY</div>
            </v-sheet>
          </div>

          <div class="tw-flex tw-justify-center tw-px-32">
            <v-sheet class="stat-card tw-max-w-[200px]" color="transparent">
              <div class="stat-number">
                {{ totalVolumeDisplay.value }} {{ totalVolumeDisplay.unit }}
              </div>
              <div class="stat-label">VOLUME</div>
            </v-sheet>
          </div>
        </div>

        <div class="chart-container">
          <div class="chart-area">
            <div class="chart-title">
              <h3 class="tw-text-white tw-text-lg tw-font-bold">
                SIGIL TRADING VIEW
              </h3>
            </div>
            <div class="tw-relative">
              <VueApexCharts
                v-if="chartSeries.length > 0 && chartSeries[0].data.length > 0"
                height="200"
                :options="chartOptions"
                :series="chartSeries"
              />
              <div
                v-else
                class="tw-flex tw-items-center tw-justify-center tw-h-[200px] tw-text-gray-400"
              >
                No trading data available
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="postcss">
.stat-card {
  @apply !tw-rounded-3xl tw-uppercase !tw-border-[#4D4D4D] !tw-border-solid !tw-border-2 tw-p-4 tw-text-center tw-text-sm  tw-transition;
  /* @apply tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-transparent tw-rounded-3xl tw-p-4 tw-w-full tw-h-[100px] tw-border-[#4D4D4D] tw-border-solid tw-border tw-text-center; */
}

.stat-number {
  @apply tw-text-white tw-text-[18px] tw-font-bold tw-mb-1;
}

.stat-label {
  @apply tw-text-white tw-text-sm tw-uppercase tw-font-normal;
}

.chart-container {
  @apply tw-relative;
}

.chart-area {
  @apply tw-relative tw-bg-[#1a1a1a] tw-rounded-xl tw-p-4;
  min-height: 260px;
}

.chart-title {
  @apply tw-text-center tw-mb-4;
}

.chart-svg {
  @apply tw-w-full tw-h-full tw-absolute tw-top-0 tw-left-0;
}

.chart-labels {
  @apply tw-absolute tw-bottom-2 tw-left-4 tw-right-4 tw-flex tw-justify-between tw-text-gray-400 tw-text-sm;
}

.chart-label-left,
.chart-label-right {
  @apply tw-text-gray-400 tw-text-sm;
}
</style>
